import type { BeforeSendFn, CaptureResult } from 'posthog-js';

type PosthogStackFrame = { filename?: string };

type PosthogException = {
  type?: string;
  value?: string;
  stacktrace?: { frames?: PosthogStackFrame[] };
};

// Recorder-internal `Failed to fetch` is unactionable network noise. Gate on the
// throw-site frame (posthog-js orders frames oldest-to-newest) so a genuine app
// fetch that merely passes through an rrweb-wrapped handler still surfaces.
const RECORDER_SOURCE_PATTERN = /posthog-recorder|rrweb|fflate/i;
const FAILED_TO_FETCH_MESSAGE = 'Failed to fetch';

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const getPrimaryException = (
  properties: Record<string, unknown>
): PosthogException | undefined => {
  const list = properties.$exception_list;
  if (!Array.isArray(list) || list.length === 0) {
    return undefined;
  }
  return list[0] as PosthogException;
};

const getOriginFilename = (exception: PosthogException | undefined): string => {
  const frames = exception?.stacktrace?.frames ?? [];
  return [...frames].reverse().find((frame) => frame.filename)?.filename ?? '';
};

const isRecorderFailedToFetch = (result: CaptureResult): boolean => {
  if (result.event !== '$exception') {
    return false;
  }
  const { properties } = result;
  const primary = getPrimaryException(properties);

  const type = primary?.type ?? readString(properties.$exception_type);
  const message = primary?.value ?? readString(properties.$exception_message);
  if (type !== 'TypeError' || message !== FAILED_TO_FETCH_MESSAGE) {
    return false;
  }

  return RECORDER_SOURCE_PATTERN.test(getOriginFilename(primary));
};

export const beforeSendToPostHog: BeforeSendFn = (result) =>
  result && isRecorderFailedToFetch(result) ? null : result;
